// Pure award-level routing shared with the portal. This is not a send grant,
// customer eligibility decision or automatic campaign enrollment instruction.
export const TITLE_TIER_POLICY_VERSION = 'sr-title-tier-2026-10-25-named-exceptions-v1'
export const TITLE_TIER_FACT_FIELDS = Object.freeze([
  'sr_level', 'championship_name', 'champion_title_line', 'governing_body',
])
const text = value => typeof value === 'string' ? value.trim() : ''
const normalize = value => text(value).normalize('NFKC').toLowerCase().replace(/[’‘]/g, "'").replace(/[_\u2010-\u2015-]+/g, ' ').replace(/\s+/g, ' ')
const RING_LEVELS = new Set(['state', 'national', 'conference'])
const LOWER_LEVEL = /^(?:region(?:al)?|district)(?:\s+(?:\d+[a-z]?|[ivx]+))?(?:\s+(?:champions?(?:hips?)?|title|finals?))?$/
const TITLE_LEVEL = /(?=\b(region(?:al)?|district|state|national|conference|world|international|league|section(?:al)?)\b(?:\s+(?:\d+[a-z]?|[ivx]+))?\s+((?:[a-z0-9&']+\s+){0,5})(?:champions?(?:hips?)?|titles?|finals?)\b)/g
const ORGANIZATION_WORDS = /\b(?:association|assn|school|college|university|institute|council|organization|governing|conference|league|state|national|world|international|region(?:al)?|district)\b/

function titleAwards(record, stripBody = true) {
  const result = []
  const bodies = stripBody ? [record.governing_body].map(normalize).filter(Boolean) : []
  for (const raw of [record.championship_name, record.champion_title_line]) {
    let title = normalize(raw)
    // The organization's geography is not the scope of the award. Remove an
    // exact known body label before examining award phrases in the title.
    for (const body of bodies) {
      // Legacy data sometimes calls the award scope itself the body ("District
      // 4" or "Regional"). Erasing that would turn a district title into a
      // State title when sr_level is stale. Only full organization-like lower
      // scope labels may be removed; uncertain short labels retain evidence.
      if (/\b(?:region(?:al)?|district)\b/.test(body)
        && !/\b(?:association|assn|league|conference|federation|council|organization|school|college|university)\b/.test(body)) continue
      title = title.split(body).join(' ')
    }
    for (const match of title.matchAll(TITLE_LEVEL)) {
      if (!ORGANIZATION_WORDS.test(match[2])) result.push({ scope: match[1], prefix: title.slice(0, match.index) })
    }
  }
  return result
}

function titleLevels(record, stripBody = true) { return new Set(titleAwards(record, stripBody).map(award => award.scope)) }

// Exact official identities, not substring/geography matches. Sources:
// https://cifss.org/cif-state-sections/ and https://cifss.org/about/
// https://wpial.org/sports/2023/7/31/other-piaa-districts.aspx
// Unknown aliases stay held. State/location and the legacy sr_governing_body
// field are not part of the complete authority read and cannot grant exceptions.
const bodyKey = value => normalize(value).replace(/[(),.]/g, ' ').replace(/\s+/g, ' ').trim()
const CIF_NAME = 'california interscholastic federation'
const PIAA_NAME = 'pennsylvania interscholastic athletic association'
const WPIAL_NAME = 'western pennsylvania interscholastic athletic league'
const CIF_SECTIONS = ['central', 'central coast', 'los angeles city', 'north coast', 'northern', 'oakland', 'sac joaquin', 'san diego', 'san francisco', 'southern']
const DISTRICTS = ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x', 'xi', 'xii']
const OFFICIAL_BODIES = new Map()
function addBodies(aliases, identity) { for (const alias of aliases) OFFICIAL_BODIES.set(bodyKey(alias), identity) }
addBodies(['cif'], { family: 'cif', kind: 'bare' })
addBodies(['cif state', 'state cif', 'cif state office', CIF_NAME, `${CIF_NAME} (cif)`], { family: 'cif', kind: 'state' })
for (const section of CIF_SECTIONS) {
  const name = `${section} section`
  addBodies([`cif ${name}`, `${name} cif`, `${CIF_NAME} ${name}`, `${CIF_NAME} (cif) ${name}`], { family: 'cif', kind: 'section', section })
}
addBodies(['cif ss', 'cifss'], { family: 'cif', kind: 'section', section: 'southern' })
addBodies(['cif oakland city section', `${CIF_NAME} oakland city section`], { family: 'cif', kind: 'section', section: 'oakland' })
const piaaAliases = ['piaa', PIAA_NAME, `${PIAA_NAME} (piaa)`]
addBodies(piaaAliases, { family: 'piaa', kind: 'state' })
for (let index = 0; index < DISTRICTS.length; index++) {
  for (const number of [String(index + 1), DISTRICTS[index]]) {
    addBodies(piaaAliases.flatMap(name => [`${name} district ${number}`, `district ${number} ${name}`]), { family: 'piaa', kind: 'district', district: index + 1 })
  }
}
addBodies(['wpial', WPIAL_NAME, `${WPIAL_NAME} (wpial)`, ...['7', 'vii'].flatMap(number => [
  `wpial (piaa district ${number})`, `${WPIAL_NAME} (piaa district ${number})`, `piaa district ${number} (wpial)`,
])], { family: 'piaa', kind: 'district', district: 7 })

const SCOPE_LEVEL = /^(state regional|region(?:al)?|district|section(?:al)?|state|national|conference|world|international|league)(?:\s+(?:\d+[a-z]?|[ivx]+))?(?:\s+(?:champions?(?:hips?)?|title|finals?))?$/
const canonicalScope = scope => /^(?:region|regional|state regional)$/.test(scope) ? 'regional' : scope === 'sectional' ? 'section' : scope
const REVIEW_REASON = 'The award level or named exception evidence is missing, conflicting or unreadable. Review it before ring artwork or sequence enrollment.'
function readableFacts(record) {
  return (record._unreadable == null || (Array.isArray(record._unreadable) && record._unreadable.every(field => typeof field === 'string')))
    && !TITLE_TIER_FACT_FIELDS.some(field => !Object.hasOwn(record, field)
      || (record[field] != null && typeof record[field] !== 'string')
      || (record._unreadable || []).includes(field))
}

function namedAwardException(record) {
  const body = OFFICIAL_BODIES.get(bodyKey(record.governing_body))
  if (!body) return null
  const level = normalize(record.sr_level)
  const explicitScope = level.match(SCOPE_LEVEL)?.[1]
  // Examine award phrases, including an official section/district attached to
  // Championship/Title wording. A body field by itself is never award evidence.
  const awards = titleAwards(record, false)
  const scopes = new Set(awards.map(award => canonicalScope(award.scope)))
  if (explicitScope) scopes.add(canonicalScope(explicitScope))
  const titles = [record.championship_name, record.champion_title_line].map(normalize)
  const scopeText = [level, ...titles].join('\n')
  const sectionAward = scopes.has('section')
  const districtAward = scopes.has('district')
  const regionalAward = scopes.has('regional')
  const candidate = body.family === 'cif' ? sectionAward || regionalAward : districtAward
  if (!candidate) return null
  if (!readableFacts(record)) return { review: true }
  // Multiple actual scopes or compound scope labels cannot be resolved by an
  // official body name, client flag or stored ring category.
  if (scopes.size !== 1 || (!explicitScope && /\b(?:region(?:al)?|district|section(?:al)?|state|national|conference|world|international|league)\b/.test(level))) return { review: true }
  const mentionedSections = new Set()
  for (const title of titles) for (const section of CIF_SECTIONS) {
    if (new RegExp(`\\b${section === 'oakland' ? 'oakland(?: city)?' : section} section\\b`).test(title)) mentionedSections.add(section)
  }
  if (titles.some(title => /\bcif ss\b|\bcifss\b/.test(title))) mentionedSections.add('southern')
  const mentionedDistricts = new Set()
  for (const match of scopeText.matchAll(/\bdistrict\s+(\d[a-z0-9/]*|[ivx]+)\b/g)) {
    mentionedDistricts.add(/^\d+$/.test(match[1]) ? Number(match[1]) : DISTRICTS.indexOf(match[1]) + 1)
  }
  if (titles.some(title => /\bwpial\b|\bwestern pennsylvania interscholastic athletic league\b/.test(title))) mentionedDistricts.add(7)
  if (body.family === 'cif') {
    if (mentionedDistricts.size || titles.some(title => /\bpiaa\b|\bpennsylvania interscholastic athletic association\b/.test(title))) return { review: true }
    if (mentionedSections.size > 1 || (body.section && [...mentionedSections].some(section => section !== body.section))) return { review: true }
    if (sectionAward) {
      // Numbered/unknown section labels are not mapped by this exact-name
      // policy and cannot override a known section's identity.
      if (/\bsection(?:al)?\s+(?:\d[a-z0-9/]*|[ivx]+)\b/.test(scopeText)) return { review: true }
      // Bare CIF has unrelated homonyms. It needs namespaced award wording,
      // not a location field or a generic Section label, to resolve identity.
      const namedCifSection = awards.some(award => canonicalScope(award.scope) === 'section' && /\b(?:cif|california interscholastic federation)\s+(?:central(?: coast)?|los angeles city|north coast|northern|oakland(?: city)?|sac joaquin|san diego|san francisco|southern)\s+$/.test(award.prefix))
      if (body.kind === 'bare' && !namedCifSection) return { review: true }
      return { reason: 'This California CIF section championship is an approved title-tier exception. Existing artwork approval rules still apply.' }
    }
    const stateRegional = explicitScope === 'state regional' || awards.some(award => canonicalScope(award.scope) === 'regional' && /\b(?:state|northern california|southern california|norcal|socal)\s+$/.test(award.prefix))
    const namedCifStateRegional = awards.some(award => canonicalScope(award.scope) === 'regional' && /\b(?:cif|california interscholastic federation)\s+state\s+$/.test(award.prefix))
    // A section's regional qualifier is not the State CIF regional title.
    if (!stateRegional || (body.kind !== 'state' && !namedCifStateRegional)) return null
    return { reason: 'This CIF State Regional championship is an approved title-tier exception. Existing artwork approval rules still apply.' }
  }
  if (mentionedSections.size || titles.some(title => /\bcif\b|\bcalifornia interscholastic federation\b/.test(title))) return { review: true }
  if ([...mentionedDistricts].some(district => district < 1 || district > 12)
    || mentionedDistricts.size > 1 || (body.district && [...mentionedDistricts].some(district => district !== body.district))) return { review: true }
  return { reason: 'This Pennsylvania PIAA athletic district championship, including WPIAL, is an approved title-tier exception. Existing artwork approval rules still apply.' }
}

export function titleTierRouting(input = {}) {
  const value = input && typeof input === 'object' && !Array.isArray(input) ? input : {}
  const record = value.raw && typeof value.raw === 'object' ? value.raw : value
  const level = normalize(record.sr_level)
  const names = titleLevels(record)
  const lower = LOWER_LEVEL.test(level) || [...names].some(value => /^(region(?:al)?|district)$/.test(value))
  let titleTier, titleTierReason
  const exception = namedAwardException(record)
  if (text(record.sr_title_tier) === 'congrats_tier') {
    titleTier = 'congrats_tier'
    titleTierReason = 'This award is saved as congratulations-only. Review its award level before changing the category.'
  } else if (exception?.review) {
    titleTier = 'review_required'
    titleTierReason = REVIEW_REASON
  } else if (exception?.reason) {
    titleTier = 'ring_tier'
    titleTierReason = exception.reason
  } else if (lower) {
    titleTier = 'congrats_tier'
    titleTierReason = 'Regional and district awards outside the approved CIF and Pennsylvania athletic district exceptions receive congratulations correspondence only, without ring artwork.'
  } else {
    const explicit = RING_LEVELS.has(level.replace(/\s+(?:champions?(?:hips?)?|title|finals?)$/, ''))
    const affirmative = explicit || [...names].some(value => RING_LEVELS.has(value))
    const unreadable = new Set(record._unreadable || [])
    const ambiguousLowerLevel = /\b(?:region(?:al)?|district)\b/.test(level) && !LOWER_LEVEL.test(level)
    // A stale State/National/Conference label cannot bypass an unrecognized
    // section award. Only the named, fully evidenced CIF exception can grant it.
    const unexceptedSection = /\bsection(?:al)?\b/.test(level) || names.has('section') || names.has('sectional')
    if (affirmative && !ambiguousLowerLevel && !unexceptedSection && !TITLE_TIER_FACT_FIELDS.some(field => unreadable.has(field))) {
      titleTier = 'ring_tier'
      titleTierReason = 'The current award level is an identified non-regional, non-district title. Existing artwork approval rules still apply.'
    } else {
      titleTier = 'review_required'
      titleTierReason = 'The award level is missing, ambiguous or unreadable. Review it before ring artwork or sequence enrollment.'
    }
  }
  return {
    titleTier, titleTierReason,
    ringArtworkEligible: titleTier === 'ring_tier',
    correspondenceRoute: titleTier === 'congrats_tier' ? 'congratulations' : titleTier === 'ring_tier' ? 'championship' : 'review',
    titleTierPolicyVersion: TITLE_TIER_POLICY_VERSION,
  }
}

export const classifyTitleTier = titleTierRouting

export function assertTitleTierSourceRecord(record, id) {
  if (!record || String(record.id ?? '') !== String(id)
    || !readableFacts(record)) {
    throw Object.assign(new Error('The complete current championship award-level record is unavailable.'), { status: 409, code: 'title_tier_review_required' })
  }
  return record
}

export function withTitleTier(record) { return { ...record, ...titleTierRouting(record) } }

export function assertRingArtworkEligible(record) {
  const routing = titleTierRouting(record)
  if (!routing.ringArtworkEligible) throw Object.assign(new Error(routing.titleTierReason), {
    status: 409,
    code: routing.titleTier === 'congrats_tier' ? 'title_tier_congratulations_only' : 'title_tier_review_required',
    titleTier: routing.titleTier,
  })
  return routing
}

// Persistence is a mirror, not authority. Unknown/ambiguous titles stay blank
// in the existing two-option HubSpot property and surface as review_required.
export function titleTierPersistence(record) {
  const routing = titleTierRouting(record)
  return { sr_title_tier: routing.titleTier === 'review_required' ? '' : routing.titleTier }
}

export function titleTierCounts(records = []) {
  const counts = { total: 0, ring_tier: 0, congrats_tier: 0, review_required: 0 }
  for (const record of records) { counts.total++; counts[titleTierRouting(record).titleTier]++ }
  return counts
}
